import styled from "styled-components";
import { device } from "../lib/breakpoints";
import Button from "../components/Button";
import { useNavigate, useRouteError } from "react-router-dom";
import { APP_NAME } from "../helpers/constants";
import { WarningOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { trackActivityEvent } from "../lib/analytics/trackActivityEvent";

const RouterErrorBoundary = ({ isNotFound = false }) => {
  const navigate = useNavigate();
  const routeError = useRouteError();

  const isDev = process.env.NODE_ENV === "development";
  const errorMessage =
    routeError instanceof Error
      ? routeError.message
      : typeof routeError === "string"
      ? routeError
      : routeError != null
      ? String(routeError)
      : null;

  useEffect(() => {
    if (isNotFound) return;
    trackActivityEvent("error_router", {
      message: (errorMessage || "Unknown route error").slice(0, 400),
      stack:
        routeError instanceof Error
          ? (routeError.stack || "").slice(0, 600)
          : undefined,
    });
  }, [isNotFound, routeError, errorMessage]);

  return (
    <Shell>
      <Card>
        {!isNotFound && (
          <>
            <IconWrap aria-hidden>
              <WarningOutlined />
            </IconWrap>
            <Brand>{APP_NAME}</Brand>
            <Title>We hit a snag</Title>
            <Subtitle>
              Something unexpected happened while loading this screen. Reloading
              usually fixes it. If it keeps happening, try the dashboard or sign
              in again.
            </Subtitle>
            <Actions>
              <Button
                variant="primary"
                rounded="slight"
                onClick={() => window.location.reload()}
                width="auto"
              >
                Reload page
              </Button>
              <Button
                variant="white"
                rounded="slight"
                onClick={() => navigate("/chat")}
                width="auto"
              >
                Go to dashboard
              </Button>
            </Actions>
            {isDev && errorMessage ? (
              <DevBox>
                <DevLabel>Development detail</DevLabel>
                <DevPre>{errorMessage}</DevPre>
              </DevBox>
            ) : null}
          </>
        )}
        {isNotFound && (
          <ErrorContext>
            <Brand>{APP_NAME}</Brand>
            <Title>Page not found</Title>
            <Subtitle>
              That URL does not match anything we know. Head back to the home
              page or your dashboard.
            </Subtitle>
            <Actions>
              <Button
                variant="primary"
                rounded="slight"
                onClick={() => navigate("/")}
                width="auto"
              >
                Return to home
              </Button>
              <Button
                variant="white"
                rounded="slight"
                onClick={() => navigate("/chat")}
                width="auto"
              >
                Go to dashboard
              </Button>
            </Actions>
          </ErrorContext>
        )}
      </Card>
    </Shell>
  );
};

export default RouterErrorBoundary;

const Shell = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: max(24px, env(safe-area-inset-top))
    max(20px, env(safe-area-inset-right)) max(24px, env(safe-area-inset-bottom))
    max(20px, env(safe-area-inset-left));
  background: var(--color-background);
  background-image: var(--gradient-background),
    radial-gradient(
      ellipse 80% 50% at 50% -20%,
      rgba(134, 228, 157, 0.25),
      transparent
    );
  box-sizing: border-box;
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  padding: 2rem 1.75rem;
  background: var(--color-bg-secondary);
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  box-shadow:
    0 12px 40px var(--shadow-light),
    0 0 0 1px rgba(44, 95, 45, 0.04);
  text-align: center;
`;

const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  margin: 0 auto 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  background: var(--color-primary-glow);
  color: var(--color-primary-darker);
  font-size: 26px;
`;

const Brand = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--color-text-secondary);
`;

const Title = styled.h1`
  margin: 0 0 0.75rem;
  font-weight: 700;
  font-size: 1.35rem;
  line-height: 1.25;
  color: var(--color-text-primary);

  @media ${device.tablet}, ${device.desktop} {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--color-text-secondary);
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;

  @media ${device.tablet}, ${device.desktop} {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
  }
`;

const DevBox = styled.div`
  margin: 1.25rem 0 0;
  padding: 12px;
  text-align: left;
  background: var(--color-bg-tertiary);
  border-radius: 8px;
  border: 1px solid var(--color-border-light);
`;

const DevLabel = styled.div`
  font-weight: 600;
  font-size: 0.75rem;
  margin-bottom: 8px;
  color: var(--color-text-secondary);
`;

const DevPre = styled.pre`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 160px;
  overflow: auto;
  font-family: ui-monospace, monospace;
`;

const ErrorContext = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;
